import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { AttestationRecipientList } from '@client/components/attestations/send/AttestationRecipientList';
import { AttestationRecipientPicker } from '@client/components/attestations/send/AttestationRecipientPicker';
import { AttestationSendFields } from '@client/components/attestations/send/AttestationSendFields';
import { useAttestationRecipients } from '@client/components/attestations/send/useAttestationRecipients';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Checkbox, Form, Tag } from 'antd';
import { DateTime } from 'luxon';
import { useId, useMemo, useRef, useState } from 'react';

type SendInput = RouterInputs['userAttestation']['sendUserAttestations'];

export const Route = createFileRoute('/console/$orgId/attestations/user-attestations/send')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Send user attestation',
        },
        permissions: ['user_attestation:create'],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <SendUserAttestation />
        </PageController>
    );
}

function SendUserAttestation() {
    const formId = useId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const [form] = Form.useForm<SendInput>();
    const onCloseRef = useRef<(() => void) | null>(null);

    const [confirmChecked, setConfirmChecked] = useState(false);
    // Any change to who receives the attestation invalidates the confirmation.
    const recipients = useAttestationRecipients({ onSelectionChange: () => setConfirmChecked(false) });
    const { recipientMode, selectedUserIds, includedUsers, excludedUsers, allBaseUsers, toggleExclusion } = recipients;
    const recipientCount = recipients.recipientCount;

    const selectedFormId = Form.useWatch('formSchemaId', form);

    const { data: formsData } = trpc.userAttestation.getActiveFormsForAttestation.useQuery({});

    const selectedForm = useMemo(() => {
        return formsData?.forms.find((f) => f.id === selectedFormId);
    }, [formsData?.forms, selectedFormId]);

    const { mutate: sendAttestations, isPending } = trpc.userAttestation.sendUserAttestations.useMutation({
        onSuccess: (result) => {
            message.success(`Successfully sent ${result.created} attestation(s)`);
            void trpcUtils.userAttestation.listUserAttestations.invalidate();
            onCloseRef.current?.();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to send attestations');
        },
    });

    const handleSubmit = (values: Omit<SendInput, 'recipients' | 'confirmRecipientCount'>) => {
        const userIds = includedUsers.map((u) => u.userId);

        sendAttestations({
            ...values,
            recipients: {
                userIds,
                sendToAll: false,
                handlingPartyIds: [],
            },
            confirmRecipientCount: userIds.length,
        });
    };

    return (
        <OverlayPage
            title="Send user attestation"
            size="large"
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => (
                <>
                    <Button type="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        loading={isPending}
                        disabled={recipientCount === 0 || !confirmChecked || !selectedFormId}
                    >
                        Send attestation
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    form={form}
                    layout="vertical"
                    onFinish={(values) => {
                        onCloseRef.current = onClose;
                        handleSubmit(values);
                    }}
                    initialValues={{
                        dueInDays: 7,
                    }}
                >
                    <AttestationSendFields
                        forms={formsData?.forms}
                        dueBaseDate={DateTime.now()}
                        recipientPicker={<AttestationRecipientPicker recipients={recipients} />}
                    />

                    {(recipientMode !== 'specific' || selectedUserIds.length > 0) && (
                        <AttestationRecipientList
                            recipientMode={recipientMode}
                            includedUsers={includedUsers}
                            excludedUsers={excludedUsers}
                            allBaseUsers={allBaseUsers}
                            onToggleExclusion={toggleExclusion}
                            title={
                                <>
                                    Send to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
                                </>
                            }
                            footer={
                                <Checkbox
                                    checked={confirmChecked}
                                    onChange={(e) => setConfirmChecked(e.target.checked)}
                                >
                                    I confirm I want to send{' '}
                                    {selectedForm ? (
                                        <>
                                            <strong>{selectedForm.formTitle}</strong>{' '}
                                            <Tag className="mx-1">v{selectedForm.version}</Tag>
                                        </>
                                    ) : (
                                        'this form'
                                    )}{' '}
                                    to{' '}
                                    <strong>
                                        {recipientCount} user{recipientCount !== 1 ? 's' : ''}
                                    </strong>
                                </Checkbox>
                            }
                        />
                    )}
                </Form>
            )}
        </OverlayPage>
    );
}
